import React, { useCallback, useEffect, useState } from "react";
import SearchComponent from "../../components/dashboard-components/SearchComponent";
import StablecoinWidget from "../../components/dashboard-components/StablecoinWidget";
import Widgets from "../../components/dashboard-components/Widgets";
import { RevenueParams, useAppRevenue } from "../../sevices/hooks/account";
import { useSearchParams } from "react-router-dom";
import RevenueTable from "../../components/dashboard-components/revenueTable";
import Calendar from "../../components/dashboard-components/Calender";

const Revenues = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize with fiat as default currencyType
  const [filters, setFilters] = useState<RevenueParams>({
    page: parseInt(searchParams.get("page") || "1"),
    pageSize: 15,
    currencyType: searchParams.get("currencyType") || "fiat",
    search: searchParams.get("search") || undefined,
    date:
      searchParams.get("date") && searchParams.get("date") !== "select"
        ? searchParams.get("date")
        : undefined,
  });

  // Set initial URL params when component mounts
  useEffect(() => {
    const initialParams: Record<string, string> = {
      page: filters.page.toString(),
      pageSize: filters.pageSize.toString(),
      currencyType: "fiat", // Always set initial currencyType to fiat
    };

    if (filters.search) initialParams.search = filters.search;
    if (filters.date && filters.date !== "select")
      initialParams.date = filters.date;

    setSearchParams(initialParams);
  }, []); // Empty dependency array ensures this only runs once on mount

  const updateSearchParams = useCallback(() => {
    const updatedParams: Record<string, string> = {
      page: filters.page.toString(),
      pageSize: filters.pageSize.toString(),
      currencyType: filters.currencyType,
    };

    if (filters.search) updatedParams.search = filters.search;
    if (filters.date && filters.date !== "select")
      updatedParams.date = filters.date;

    setSearchParams(updatedParams);
  }, [filters, setSearchParams]);

  const handleDateChange = useCallback(
    (filterType: string, from?: string, to?: string) => {
      setFilters((prev) => {
        const updatedFilters = {
          ...prev,
          date: filterType !== "select" ? filterType : undefined,
          page: 1,
        };
        return updatedFilters;
      });
      updateSearchParams();
    },
    [updateSearchParams]
  );

  const handleCurrencyTypeChange = useCallback(
    (currency: string) => {
      setFilters((prev) => {
        const updatedFilters = {
          ...prev,
          currencyType: currency,
          page: 1,
        };
        return updatedFilters;
      });
      updateSearchParams();
    },
    [updateSearchParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setFilters((prev) => {
        const updatedFilters = {
          ...prev,
          page: newPage,
        };
        return updatedFilters;
      });
      updateSearchParams();
    },
    [updateSearchParams]
  );

  // Initialize currentTab with filters.currencyType
  const [currentTab, setCurrentTab] = useState(filters.currencyType);

  // Keep currentTab in sync with filters.currencyType
  useEffect(() => {
    setCurrentTab(filters.currencyType);
  }, [filters.currencyType]);

  const { data: revenue, isLoading } = useAppRevenue({
    currencyType: filters.currencyType,
    page: filters.page,
    pageSize: filters.pageSize,
    date: filters.date,
    search: filters.search,
  });

  return (
    <div className="h-screen w-full bg-#fff p-5 md:pr-5 md:pl-8">
      <div className="flex flex-row items-center justify-between my-5">
        <h1 className="font-bold text-2xl text-[#071827] md:pb-2">Revenue</h1>
        <div className="toggle w-60 h-12 md:h-10 flex border border-[#0eb683] self-center justify-between rounded-lg cursor-pointer p-1">
          <div
            className={`px-2 flex-1 whitespace-nowrap flex items-center justify-center transition-all duration-500 rounded-md ${
              currentTab === "fiat" ? "bg-[#0eb683] text-white" : ""
            }`}
            onClick={() => handleCurrencyTypeChange("fiat")}
          >
            Fiat
          </div>
          <div
            className={`px-2 flex-1 whitespace-nowrap flex items-center justify-center transition-all duration-500 rounded-md ${
              currentTab === "stablecoin" ? "bg-[#0eb683] text-white" : ""
            }`}
            onClick={() => handleCurrencyTypeChange("stablecoin")}
          >
            Stablecoin
          </div>
        </div>
      </div>

      <div>
        <div className="flex gap-3">
          <Widgets
            color="#fff"
            currency="Revenue"
            symbol="₦"
            border={"1px solid #0EB683"}
            redirect={"/dashboard/accounts/usd"}
          />
          <Widgets
            color="#C4FFF4"
            currency="USD Collections"
            symbol="$"
            border={"1px solid #C4FFF4"}
            redirect={"/dashboard/accounts/usd"}
          />
          <Widgets
            color="#FFDECF"
            currency="GBP Collections"
            symbol="£"
            border={"1px solid #FFDECF"}
            redirect={"/dashboard/accounts/usd"}
          />
          <Widgets
            color="#E8EAFF"
            currency="EUR Collections"
            symbol="€"
            border={"1px solid #E8EAFF"}
            redirect={"/dashboard/accounts/usd"}
          />
        </div>

        <div className="mt-5 flex items-center gap-3 mb-2">
          <SearchComponent placeholder={"Search"} />
          <Calendar
            onDateChange={handleDateChange}
            initialFilter={filters.date || ""}
          />
        </div>

        <RevenueTable
          data={revenue?.data?.data}
          isLoading={isLoading}
          page={filters.page}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default Revenues;
